import { useId } from 'react';

import type { MessageDraft, OfferDraft, Proposal } from '../../api/agentSchemas';
import { OfferDayPicker } from '../../components/OfferDayPicker';
import { offerDateInput } from '../../lib/offer-form';
import { personalize } from './conversation';

const MAX_TITLE = 80;
const MAX_DESCRIPTION = 280;
const MAX_MESSAGE = 1000;

type OfferEditorProps = {
  draft: OfferDraft;
  disabled: boolean;
  onChange: (draft: OfferDraft) => void;
};

/** The oferta exactly as Ofertas would create it, every field editable before approving. */
export function OfferDraftEditor({ draft, disabled, onChange }: OfferEditorProps) {
  const id = useId();
  const set = <Key extends keyof OfferDraft>(key: Key, value: OfferDraft[Key]) => onChange({ ...draft, [key]: value });
  return (
    <fieldset className="form-grid cols-2 agents-draft" disabled={disabled}>
      <legend className="section-label">Borrador (puedes editarlo)</legend>
      <div className="field span-2">
        <label className="field-label" htmlFor={`${id}-title`}>Nombre de la oferta</label>
        <input id={`${id}-title`} value={draft.title} maxLength={MAX_TITLE} onChange={(event) => set('title', event.target.value)} />
      </div>
      <div className="field span-2">
        <label className="field-label" htmlFor={`${id}-description`}>Descripción</label>
        <input
          id={`${id}-description`}
          value={draft.description}
          maxLength={MAX_DESCRIPTION}
          onChange={(event) => set('description', event.target.value)}
        />
      </div>
      <div className="field">
        <label className="field-label" htmlFor={`${id}-percent`}>Descuento (%)</label>
        <input
          id={`${id}-percent`}
          type="number"
          inputMode="numeric"
          min={1}
          max={90}
          value={draft.percentOff}
          onChange={(event) => set('percentOff', Math.round(Number(event.target.value)))}
        />
      </div>
      <OfferDayPicker days={draft.validDays} onChange={(days) => set('validDays', days)} />
      <div className="field">
        <label className="field-label" htmlFor={`${id}-starts`}>Empieza</label>
        <input
          id={`${id}-starts`}
          type="datetime-local"
          value={offerDateInput(draft.startsAt)}
          onChange={(event) => set('startsAt', fromDateInput(event.target.value, draft.startsAt))}
        />
      </div>
      <div className="field">
        <label className="field-label" htmlFor={`${id}-until`}>Termina</label>
        <input
          id={`${id}-until`}
          type="datetime-local"
          value={offerDateInput(draft.validUntil)}
          onChange={(event) => set('validUntil', fromDateInput(event.target.value, draft.validUntil))}
        />
      </div>
    </fieldset>
  );
}

type MessageEditorProps = {
  proposal: Proposal;
  draft: MessageDraft;
  disabled: boolean;
  onChange: (draft: MessageDraft) => void;
};

/** The WhatsApp text, who gets it, and how it reads for the first person on the list. */
export function MessageDraftEditor({ proposal, draft, disabled, onChange }: MessageEditorProps) {
  const id = useId();
  const recipients = proposal.recipients ?? [];
  const keyOf = (recipient: (typeof recipients)[number]) => recipient.bookingId ?? recipient.customerId;
  // Nobody without a WhatsApp number can get the message, so they are never counted in.
  const reachable = recipients.filter((recipient) => recipient.phone);
  const included = reachable.filter((recipient) => !draft.skip.includes(keyOf(recipient)));
  const sample = included[0];
  const toggle = (key: string) =>
    onChange({
      ...draft,
      skip: draft.skip.includes(key) ? draft.skip.filter((skipped) => skipped !== key) : [...draft.skip, key],
    });

  return (
    <fieldset className="form-grid cols-2 agents-draft" disabled={disabled}>
      <legend className="section-label">Borrador (puedes editarlo)</legend>
      <div className="field span-2">
        <label className="field-label" htmlFor={`${id}-text`}>Mensaje</label>
        <textarea
          id={`${id}-text`}
          rows={4}
          value={draft.text}
          maxLength={MAX_MESSAGE}
          aria-describedby={`${id}-hint`}
          onChange={(event) => onChange({ ...draft, text: event.target.value })}
        />
        <p className="row-sub" id={`${id}-hint`}>
          Deja {'{nombre}'} donde va el nombre de cada persona.
        </p>
      </div>
      {sample ? (
        <div className="span-2 agents-preview">
          <p className="field-label">Así le llega a {sample.name.split(' ')[0]}</p>
          <p className="agents-bubble">{personalize({ ...proposal, draft }, sample)}</p>
        </div>
      ) : null}
      <div className="span-2">
        <p className="field-label">
          Para {included.length} de {reachable.length}
          {recipients.length > reachable.length ? ` (${recipients.length - reachable.length} sin WhatsApp)` : ''}
        </p>
        <ul className="agents-recipients">
          {recipients.map((recipient) => {
            const key = keyOf(recipient);
            return (
              <li key={key}>
                <label className="agents-recipient">
                  <input
                    type="checkbox"
                    checked={Boolean(recipient.phone) && !draft.skip.includes(key)}
                    disabled={!recipient.phone}
                    onChange={() => toggle(key)}
                  />
                  <span className="grow">
                    <span className="row-title">{recipient.name}</span>
                    <span className="row-sub">
                      {recipient.detail}
                      {recipient.phone ? '' : ' · sin WhatsApp'}
                    </span>
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      </div>
    </fieldset>
  );
}

/** A datetime-local value back to ISO; an emptied field keeps the last good date. */
function fromDateInput(value: string, previous: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? previous : date.toISOString();
}
